import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import { Box, Button, Card, Tab, Tabs, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { errorToast, successToast } from "../../helpers";
import { useLazyBookingCsvQuery } from "../../services/booking";
import OngoingBookingsList from "../../features/bookingDetails";
import CompletedBookingsList from "../../features/bookingDetails/completed";
import UpcomingBookingsList from "../../features/bookingDetails/upcoming";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CancelledBookingListing from "../../features/bookingDetails/cancelledBooking";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageAllBookings = () => {
  const [exportCsv] = useLazyBookingCsvQuery();
  const [mainTabValue, setMainTabValue] = useState<number>(0); // 0: Bookings, 1: Online Bookings
  const [subTabValue, setSubTabValue] = useState<number>(0); // 0: Ongoing, 1: Completed, 2: Upcoming, 3: Cancelled
  const navigate = useNavigate();
  const { state } = useLocation();

  const bookingTypes: any = {
    0: 6, // Ongoing
    1: 3, // Completed
    2: 7, // Upcoming
    3: 5, // Cancelled
  };

  const handleMainTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setMainTabValue(newValue);
    // Reset sub-tab when switching between Bookings and Online Bookings
    setSubTabValue(0);
  };

  const handleSubTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setSubTabValue(newValue);
  };

  const handleExportCsv = async () => {
    try {
      const res = await exportCsv({
        isOnline: mainTabValue === 1, // true for Online Bookings, false for regular Bookings
        bookingType: bookingTypes[subTabValue] || 6,
      }).unwrap();

      const parsedRes = typeof res === "string" ? JSON.parse(res) : res;

      if (parsedRes && parsedRes.message && parsedRes.message.s3Location) {
        window.open(parsedRes.message.s3Location);
        successToast("CSV exported successfully");
      } else {
        errorToast("Error: s3Location not found in response");
      }
    } catch (error: any) {
      errorToast(error?.data?.message);
    }
  };

  useEffect(() => {
    if (state) {
      // Handle state from navigation if needed
      // You can pass state like { mainTab: 0, subTab: 1 } to set both tabs
      if (typeof state === "object") {
        if (state.mainTab !== undefined) setMainTabValue(state.mainTab);
        if (state.subTab !== undefined) setSubTabValue(state.subTab);
      } else {
        setSubTabValue(Number(state));
      }
    }
  }, [state]);

  const getMainTabLabel = (index: number) => {
    return index === 0 ? "Bookings" : "Online Bookings";
  };

  const getSubTabLabel = (index: number) => {
    const labels = [
      "On-Going Bookings",
      "Completed Bookings",
      "Upcoming Bookings",
      "Cancelled Bookings",
    ];
    return labels[index];
  };

  return (
    <MainContainer>
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">Manage {getMainTabLabel(mainTabValue)}</h1>
        </div>

        {/* Main Tabs for Bookings vs Online Bookings */}
        <Card className="cards" sx={{ mb: 2 }}>
          <Box className="custom_tabs">
            <Tabs
              value={mainTabValue}
              onChange={handleMainTabChange}
              variant="fullWidth"
              aria-label="main booking tabs"
              className="custom_tabs_links"
            >
              <Tab label="Bookings" {...a11yProps(0)} />
              <Tab label="Online Bookings" {...a11yProps(1)} />
            </Tabs>
          </Box>
        </Card>

        {/* Sub Tabs for Booking Status */}
        <Card className="cards">
          <Box className="custom_tabs">
            <Box className="flx_sc">
              <Tabs
                value={subTabValue}
                onChange={handleSubTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="booking status tabs"
                className="custom_tabs_links"
              >
                <Tab label="On-Going Bookings" {...a11yProps(0)} />
                <Tab label="Completed Bookings" {...a11yProps(1)} />
                <Tab label="Upcoming Bookings" {...a11yProps(2)} />
                <Tab label="Cancelled Bookings" {...a11yProps(3)} />
              </Tabs>
              <Button className="btn btn_primary" onClick={handleExportCsv}>
                <FileDownloadIcon />
                Export CSV
              </Button>
            </Box>

            {/* Ongoing Bookings */}
            <CustomTabPanel value={subTabValue} index={0}>
              <OngoingBookingsList
                value={subTabValue}
                online={mainTabValue === 1}
              />
            </CustomTabPanel>

            {/* Completed Bookings */}
            <CustomTabPanel value={subTabValue} index={1}>
              <CompletedBookingsList
                value={subTabValue}
                online={mainTabValue === 1}
              />
            </CustomTabPanel>

            {/* Upcoming Bookings */}
            <CustomTabPanel value={subTabValue} index={2}>
              <UpcomingBookingsList
                value={subTabValue}
                online={mainTabValue === 1}
              />
            </CustomTabPanel>

            {/* Cancelled Bookings */}
            <CustomTabPanel value={subTabValue} index={3}>
              <CancelledBookingListing
                value={subTabValue}
                online={mainTabValue === 1}
              />
            </CustomTabPanel>
          </Box>
        </Card>
      </div>
    </MainContainer>
  );
};

export default ManageAllBookings;
